package com.bikompanion.ui.watch

import com.mmk.kmpnotifier.KMPNotifier
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.notifications.Notification
import org.w3c.notifications.NotificationOptions

private const val NotificationClickMessageType = "station-watch-notification-click"
private const val ServiceWorkerFile = "station-watch-service-worker.js"

private var serviceWorkerRegistrationPromise: dynamic = null
private var serviceWorkerMessageListenerInstalled = false

internal fun resetStationWatchNotificationWebState() {
    serviceWorkerRegistrationPromise = null
    serviceWorkerMessageListenerInstalled = false
}

/**
 * Asks for the permission a watch needs. The notifier calls `Notification` unguarded, which throws
 * in a browser without the API, and the watch there reports through
 * [postStationWatchNotification]'s alert instead.
 */
fun requestStationWatchPermission(onResult: (Boolean) -> Unit) {
    if (notificationsSupported()) {
        KMPNotifier.permissionUtil.askNotificationPermission(onResult)
    } else {
        onResult(true)
    }
}

internal actual fun prepareStationWatchNotificationClicks() {
    deliverStationTargetFromUrl(window.location.href, clearFromAddressBar = true)
    if (!serviceWorkersSupported()) return

    ensureServiceWorkerRegistration().catch { _: dynamic -> Unit }
    if (serviceWorkerMessageListenerInstalled) return

    serviceWorkerMessageListenerInstalled = true
    window.navigator.asDynamic().serviceWorker.addEventListener("message", { event: dynamic ->
        if (event.data?.type == NotificationClickMessageType) {
            (event.data?.targetUrl as? String)?.let { deliverStationTargetFromUrl(it) }
        }
    })
}

internal actual fun postStationWatchNotification(
    id: Int,
    title: String,
    body: String,
    target: StationNotificationTarget,
) {
    if (!notificationsSupported() || Notification.permission.toString() != "granted") {
        window.alert(body)
        return
    }

    val targetUrl = stationNotificationTargetUrl(target, window.location.href)
    if (!serviceWorkersSupported()) {
        showPageNotification(id, title, body, target)
        return
    }

    ensureServiceWorkerRegistration()
        .then { registration: dynamic ->
            registration.showNotification(
                title,
                persistentNotificationOptions(body, id.toString(), targetUrl),
            )
        }.catch { _: dynamic ->
            showPageNotification(id, title, body, target)
        }
}

internal fun stationNotificationTargetUrl(
    target: StationNotificationTarget,
    currentUrl: String,
): String {
    val url = newUrl(currentUrl)
    target.toPayloadData().forEach { (key, value) -> url.searchParams.set(key, value) }
    return url.href as String
}

internal fun stationNotificationTargetFromUrl(urlValue: String): StationNotificationTarget? {
    val url = runCatching { newUrl(urlValue) }.getOrNull() ?: return null
    val payload = buildMap<String, Any?> {
        notificationPayloadKeys.forEach { key ->
            (url.searchParams.get(key) as? String)?.let { put(key, it) }
        }
    }
    return payload.toStationNotificationTarget()
}

private fun deliverStationTargetFromUrl(
    urlValue: String,
    clearFromAddressBar: Boolean = false,
) {
    val target = stationNotificationTargetFromUrl(urlValue) ?: return
    selectStationFromNotification(target)
    if (clearFromAddressBar) clearStationTargetFromAddressBar()
}

private fun clearStationTargetFromAddressBar() {
    val url = newUrl(window.location.href)
    notificationPayloadKeys.forEach { key -> url.searchParams.delete(key) }
    window.history.replaceState(
        window.history.state,
        document.title,
        "${url.pathname}${url.search}${url.hash}",
    )
}

private fun ensureServiceWorkerRegistration(): dynamic {
    if (serviceWorkerRegistrationPromise == null) {
        serviceWorkerRegistrationPromise =
            window.navigator
                .asDynamic()
                .serviceWorker
                .register(
                    newUrl(ServiceWorkerFile, document.baseURI).href,
                    js("({ updateViaCache: 'none' })"),
                ).catch { error: dynamic ->
                    serviceWorkerRegistrationPromise = null
                    js("Promise.reject(error)")
                }
    }
    return serviceWorkerRegistrationPromise
}

private fun showPageNotification(
    id: Int,
    title: String,
    body: String,
    target: StationNotificationTarget,
) {
    val notification = Notification(title, notificationOptions(body, id.toString()))
    notification.onclick = {
        window.focus()
        selectStationFromNotification(target)
        notification.close()
    }
}

private fun notificationsSupported(): Boolean = js("typeof Notification !== 'undefined'")

private fun serviceWorkersSupported(): Boolean = js("'serviceWorker' in navigator")

private fun newUrl(value: String): dynamic = js("new URL(value)")

private fun newUrl(
    value: String,
    base: String,
): dynamic = js("new URL(value, base)")

@Suppress("UNUSED_PARAMETER")
private fun notificationOptions(
    body: String,
    tag: String,
): NotificationOptions = js("({ body: body, tag: tag })")

@Suppress("UNUSED_PARAMETER")
private fun persistentNotificationOptions(
    body: String,
    tag: String,
    targetUrl: String,
): dynamic = js("({ body: body, tag: tag, data: { targetUrl: targetUrl } })")

private val notificationPayloadKeys =
    listOf(
        NetworkIdKey,
        StationIdKey,
        StationNameKey,
        LatitudeKey,
        LongitudeKey,
        FreeBikesKey,
        EmptySlotsKey,
        ElectricBikesKey,
        SupportsElectricBikesKey,
    )
